import React, { useRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import VoSearchShadowRoot from './vo-search-shadow-root';
import VisitingObjectsSearch, { resolveRenderShape, rendererForShape } from './index';
import { use_base_url } from './hooks/use_base_url';
import { fetchSharedConfig, sendClientErrorBeacon, resetSharedConfigCache } from './search_fetch';
import { debugLog, debugWarn } from './debug';
import { parseShapeAttr } from './shape_attr';
import { mountWidgetInShadowHost } from './shadow_mount';
import cssText from './styles/vo-search.css';

// Inject styles when script loads (only once)
if (typeof document !== 'undefined') {
  const styleId = 'vo-search-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = cssText;
    document.head.appendChild(style);
  }
}

// Track if we've already created a search instance
let hasSearchInstance = false;
let searchContainer = null;
let searchConfig = null;
// What the bootstrap hands every widget it mounts, as one object with a stable
// identity: index.jsx's merge is a useMemo keyed on `ui`, so a fresh literal per
// render would recompute the whole config every render.
//
// Rebuilt once per bootstrap by initialize_search. Empty for the overwhelming
// majority of embeds, which carry no marker.
let widgetUi = {};

// widgetUiOverrides is what the bootstrap parsed off the embed that the RENDERED
// widget needs, as opposed to what only the bootstrap's own chrome needs.
//
// The distinction is the whole of item #16466. index.jsx refetches the index
// config itself and builds the config use_search hands to buildRequestHeaders
// from `{...apiConfig, ...ui}`; the module-level `searchConfig` this file keeps
// is used for shape and colour decisions here and is never read by the React
// tree. Anything a request has to carry therefore has to travel through `ui`.
export function widgetUiOverrides(overrides = {}) {
  const ui = {};
  if (overrides.previewMarker) {
    ui.previewMarker = overrides.previewMarker;
  }
  return ui;
}
// Container-level overrides (e.g., data-modal-trigger-icon-color for dashboard)
let containerOverrides = {};
// The client token for the current embed, needed to mount inline placements.
let currentClientPublicToken = null;

// The retired display_mode enum. It survives in exactly two places: as the
// vocabulary of the vo-search-open event payload, which the shared overlay and
// cached snippets still read, and as a token a stored placement may still carry.
// It is no longer how any placement's shape is decided.
const LEGACY_DISPLAY_MODES = ['modal', 'inline', 'tile'];

// resolvePlacementShape resolves what a placement renders, as the same
// trigger/surface/layout triple the instance shape uses.
//
// A placement used to be modal|inline|tile, resolved here, while the instance
// was a triple resolved in index.jsx. Two resolvers over two value spaces is
// exactly how the two axes drifted apart, and it left a placement unable to
// express a Search Panel, an attached grid, or any small-screen behaviour. The
// placement axis now speaks one vocabulary with everything else (decision
// #5067, extending #4963).
//
// Precedence per attribute: what the placement states, then the triple its
// legacy mode maps to, then the instance shape. A placement stating nothing
// therefore renders exactly what the instance renders.
//
// The server already resolves all of this and sends it as placement.shape; the
// rest of this function is the fallback for a config that predates that, so a
// cached bundle keeps resolving the way the server would.
export function resolvePlacementShape(placement, config) {
  const p = placement || {};
  const stated = p.shape || p;
  const instance = resolveRenderShape(config || {});

  const hasAttribute =
    stated.trigger != null || stated.surface != null || stated.layout != null;

  if (hasAttribute) {
    // Unstated attributes inherit; a stated-but-unknown one is repaired to its
    // attribute default by resolveRenderShape rather than blanking the widget.
    return resolveRenderShape({
      trigger: stated.trigger != null ? stated.trigger : instance.trigger,
      surface: stated.surface != null ? stated.surface : instance.surface,
      layout: stated.layout != null ? stated.layout : instance.layout,
    });
  }

  // A legacy placement mode maps through the same legacy table the instance
  // shape uses, by handing it to the same resolver as a displayMode-only config.
  // An unknown token falls through to the instance rather than being coerced,
  // exactly as the retired resolver did: a typo must not repoint a placement.
  if (LEGACY_DISPLAY_MODES.includes(p.mode)) {
    return resolveRenderShape({ displayMode: p.mode });
  }

  return instance;
}

// openSearchWithShape opens the shared overlay with the shape the trigger's
// placement resolved to, so the overlay renders what that placement configured
// rather than re-deriving it from a token.
//
// The legacy {mode} is carried BESIDE the shape, not instead of it: snippets
// cached on customer sites dispatch and read that token, and an overlay bundle
// already deployed reads it too. Both stay on the payload.
export function openSearchWithShape(shape) {
  const resolved = resolveRenderShape(shape);
  window.dispatchEvent(
    new CustomEvent('vo-search-open', {
      detail: { shape: resolved, mode: openModeForShape(resolved) },
    })
  );
}

// openSearch is the legacy entry point, taking a display-mode token. It maps the
// token to a shape so a caller still speaking the old vocabulary produces the
// same payload as one speaking the new one.
export function openSearch(mode) {
  const m = LEGACY_DISPLAY_MODES.includes(mode) ? mode : 'modal';
  openSearchWithShape(resolveRenderShape({ displayMode: m }));
}

// openModeForShape picks the legacy open-event token a resolved shape maps to,
// for the {mode} half of the payload.
export function openModeForShape(shape) {
  return rendererForShape(shape) === 'tile' ? 'tile' : 'modal';
}

// parseShapeAttr reads a first-party `trigger/surface/layout` attribute into
// the triple the widget's resolver takes (decision #5050). It lives in
// shape_attr.js because the demo bundle needs the same reading, and importing
// it from here would drag this whole bootstrap into that bundle. Re-exported so
// every existing caller and test keeps its import.
export { parseShapeAttr };

// Exported for tests, which exercise the real parser rather than a
// re-implemented copy of its logic (the #8174 lesson).
export { getScriptConfig };

// The shared shadow-host mounter lives in its own module for the same reason
// (item #8774): the demo bootstrap mounts through it, and importing this file
// would run the customer bootstrap on a demo page. Re-exported so a caller
// reaching for the mount path finds it beside mountPlacement.
export { mountWidgetInShadowHost };

// placementKey identifies WHICH placement put a host on the page, so a second
// bootstrap pass can tell its own work from another placement's.
//
// A bare `.vo-search-inline-host` check is not enough: two placements can
// address one target from the same side, and matching on the class alone would
// let the first silently satisfy the second, so only one of the two would ever
// mount. The key is the placement's own address -- the selector or the mount
// name -- because that is what stays stable across a re-run.
export function placementKey(placement) {
  const p = placement || {};
  if (p.mount) return `mount:${p.mount}`;
  if (p.selector) return `sel:${p.selector}`;
  return null;
}

// hasAdjacentPlacement asks whether this placement already sits beside the
// target on the side it mounts to.
//
// It walks the contiguous run of stamped placement hosts rather than checking
// one sibling, because placements stack: two rules mounting `before` the same
// element leave [hostA, hostB, target], so the second one inserted is the only
// immediate sibling and the first would re-insert on every pass. The walk stops
// at the first element that is not a placement host, so it never wanders into
// the customer's own markup.
//
// Element siblings, not raw nodes: a text node between the host and its target
// is whitespace in the customer's markup, and letting it hide the host is how
// the guard silently stops guarding.
function hasAdjacentPlacement(target, at, key) {
  if (!key || at !== 'before' && at !== 'after') return false;
  const step = at === 'before' ? 'previousElementSibling' : 'nextElementSibling';
  let el = target[step];
  while (el && el.dataset && el.dataset.voSearchPlacement) {
    if (el.dataset.voSearchPlacement === key) return true;
    el = el[step];
  }
  return false;
}

// mountPlacement puts a search widget rendered into its own shadow root at the
// target element, so one snippet can drop the widget in place at any placement
// the instance configures.
//
// shape is the placement's own resolved triple. It used to be null for every
// customer placement -- the general path existed but only first-party VO chrome
// pinning its own slot (decision #5050) ever reached it, so a customer placement
// could only ever render the instance shape. Passing the placement's shape is
// what makes one instance able to render several.
//
// `at` is where the root goes relative to the target, which is what the
// placement's own action says: a replace rule stands in for the element, an add
// rule sits beside it and leaves it alone.
export function mountPlacement(target, shape = null, narrow = null, overlayScope = 'page', at = 'replace', key = null) {
  if (!target || !target.parentNode) return false;
  // Already satisfied by an earlier pass is a match, not a miss -- the #8713
  // precedent. Reporting it as a miss makes the re-run conclude the instance
  // has no placements and populate the snippet container with a stray trigger.
  //
  // Only an add rule needs this. A replace rule consumes its target, so the
  // selector cannot re-match and there is nothing to guard against.
  if (hasAdjacentPlacement(target, at, key)) return true;
  const host = document.createElement('div');
  host.className = 'vo-search-inline-host';
  if (key) host.dataset.voSearchPlacement = key;
  if (at === 'before') {
    target.parentNode.insertBefore(host, target);
  } else if (at === 'after') {
    target.parentNode.insertBefore(host, target.nextSibling);
  } else {
    target.parentNode.replaceChild(host, target);
  }
  // No mode is passed to the widget: the merge recomputes config.mode from the
  // resolved shape unconditionally, so the ui.mode this used to send was
  // silently discarded and only implied a client mode input that does not exist
  // (decision #4963).
  //
  // A placement scoped to itself keeps its overlay inside its own shadow root
  // instead of at the top of the document, so two overlays on one page do not
  // fight over document.body and a panel demo does not black out the page. The
  // shared mounter is what applies that scope.
  mountWidgetInShadowHost(host, {
    overlayScope,
    render: ({ portalTarget }) => (
      <VisitingObjectsSearch
        clientPublicToken={currentClientPublicToken}
        ui={widgetUi}
        shapeOverride={shape}
        narrowOverride={narrow}
        portalTarget={portalTarget}
      />
    ),
  });
  return true;
}

/**
 * Whether this instance already put a placement on the page.
 *
 * The two things a placement leaves behind: a shadow-root host where it mounted
 * the widget in place, and a trigger container where it wired one. Either means
 * the instance renders somewhere, so the snippet container needs no fallback --
 * even on a pass where nothing matched, which is the normal state of affairs
 * once a named mount has consumed its own mount point (item #8713).
 *
 * @param {Document} [doc=document] - document to search
 * @returns {boolean} true when a placement of this instance is already mounted
 */
export function hasMountedPlacement(doc = document) {
  return Boolean(doc.querySelector('.vo-search-inline-host, .vo-search-trigger-container'));
}

// placementNarrowOverride reads the collapse the server resolved for this
// placement, or null when it configures none.
//
// Null is a real answer, not a missing one: per decision #5057 a placement that
// declares no small-screen shape renders its base shape at every width, even
// when the instance collapses. Inheriting would silently change how a live
// placement renders on a phone.
export function placementNarrowOverride(placement) {
  const narrow = placement && placement.narrowShape;
  if (!narrow) return null;
  return { shape: narrow, breakpointPx: placement.narrowBreakpointPx };
}

// placementTargets finds the elements a placement applies to. A placement is
// addressed either by a selector matching markup the customer already has, or by
// a mount name they added a container for -- <div data-vo-search-mount="...">.
//
// The mount name is a NAME, never a shape (decisions #4963 and #5050): the
// customer's markup says where the widget goes, and the server says what it
// looks like there.
export function placementTargets(placement, doc = document) {
  const p = placement || {};
  if (p.mount) {
    return Array.from(doc.querySelectorAll(`[data-vo-search-mount="${p.mount}"]`));
  }
  if (p.selector) {
    return Array.from(doc.querySelectorAll(p.selector));
  }
  return [];
}

/**
 * Normalize legacy refParam format.
 * Legacy snippets use "?ref=search" (with ?) while new system expects "ref=search" (without ?)
 * @param {string|null} param - The ref parameter from legacy data attribute
 * @returns {string} Normalized param without leading "?"
 */
export function normalizeRefParam(param) {
  if (!param) return '';
  return param.startsWith('?') ? param.slice(1) : param;
}

/**
 * Get configuration from script tag or fallback to div container.
 * Supports both new script-tag embed and legacy div-based embed.
 * @returns {{ clientPublicToken: string } | null}
 */
function getScriptConfig() {
  // Try to find our script tag by class or by src pattern
  const script = document.currentScript ||
    document.querySelector('script.visitingobjects-search') ||
    document.querySelector('script[src*="vo-search.js"]');

  if (script && script.getAttribute('data-public-token')) {
    return {
      clientPublicToken: script.getAttribute('data-public-token'),
      // The script tag is where the marker is actually served (item #16466).
      // The div branch below has read it since #16090 and every guard in this
      // chain exercised that branch, so the one real embeds take dropped the
      // marker before any downstream link could see it. Falsy when absent, so
      // the header code has one condition to test.
      previewMarker: script.getAttribute('data-preview-marker') || null,
    };
  }

  // Fallback: look for div container (backward compat)
  // Supports both new class-based selector and legacy ID-based selector
  const div = document.querySelector('#visitingobjects-search, .visitingobjects-search, [data-vo-search="true"]');
  if (div && div.getAttribute('data-public-token')) {
    return {
      clientPublicToken: div.getAttribute('data-public-token'),
      modalTriggerIconColor: div.getAttribute('data-modal-trigger-icon-color') || null,
      // First-party chrome only (decision #5050). A customer snippet never
      // carries these; VO's own pages use them to say what a slot they own
      // renders, and how its overlay behaves.
      shape: parseShapeAttr(div.getAttribute('data-vo-search-shape')),
      // 'self' keeps the overlay inside the slot instead of over the document
      // (decision #5067). Only the two known values are honoured, so a typo
      // falls back to the page overlay every embed in the wild uses rather
      // than producing a third behaviour (item #8781).
      overlayScope:
        div.getAttribute('data-vo-search-overlay-scope') === 'self' ? 'self' : 'page',
      container: div,  // Keep reference to container for backward compat
      // Legacy attributes for result link configuration
      resultLinkRootPath: div.getAttribute('data-result-link-root-path') || null,
      resultLinkParam: div.getAttribute('data-result-link-param') || null,
      // Server-minted marker that keeps preview traffic out of the customer's
      // analytics. Any page can set this attribute; it is only honoured if it
      // verifies against the service secret for this exact token, so an
      // arbitrary value fails verification and the request logs as normal.
      // Falsy when absent, so the header code has one condition to test.
      previewMarker: div.getAttribute('data-preview-marker') || null,
    };
  }

  return null;
}

// Default DOM replacement rules to use if none are provided by the server
const DEFAULT_DOM_REPLACEMENT_RULES = [
  {
    selector: '.gh-search',
    action: 'replace',
    type: 'icon'
  },
  {
    selector: '.search-field[data-ghost-search]',
    action: 'clone',
    parentSelector: '.form-wrapper',
    searchFieldSelector: '.search-field',
    searchButtonSelector: '.search-button'
  },
  {
    selector: 'a[href="#/search"]',
    action: 'replace',
    type: 'icon'
  }
];

// Function to fetch the config from the server
export async function fetchSearchConfig(clientPublicToken) {
  if (!clientPublicToken) return null;

  // Get the base URL based on window location (also used by the failure beacon).
  const { hostname } = window.location;
  const isDev =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.ts.net') ||
    hostname.endsWith('.ngrok-free.dev') ||
    hostname.endsWith('.ngrok.io') ||
    hostname.endsWith('.ngrok.app');
  const baseUrl = isDev ? '' : 'https://visitingobjects.com';

  try {
    // Shared with the overlay root and every placement root: one embed resolves
    // its config once, however many widgets that config ends up mounting.
    const data = await fetchSharedConfig(
      `${baseUrl}/api/search/index/config`,
      clientPublicToken,
      () => {
        const formData = new FormData();
        formData.append('clientPublicToken', clientPublicToken);
        return formData;
      },
      // The bootstrap has always failed straight through to its default rules
      // rather than retrying; sharing the request must not change that.
      { retries: 0 }
    );
    debugLog('[vo-search-standalone] Received config from server:', {
      theme: data.theme,
      primaryColor: data.primaryColor,
      backgroundColor: data.backgroundColor,
      modalTriggerIconColor: data.modalTriggerIconColor,
      domReplacementRules: data.domReplacementRules ? 'present' : 'absent'
    });

    // Parse DOM replacement rules if present
    if (data.domReplacementRules) {
      try {
        data.parsedDOMReplacementRules = JSON.parse(data.domReplacementRules);
      } catch (e) {
        console.error('Error parsing DOM replacement rules:', e);
        data.parsedDOMReplacementRules = DEFAULT_DOM_REPLACEMENT_RULES;
      }
    } else {
      data.parsedDOMReplacementRules = DEFAULT_DOM_REPLACEMENT_RULES;
    }

    return data;
  } catch (error) {
    console.error('Error fetching search config:', error);
    sendClientErrorBeacon({
      baseUrl,
      token: clientPublicToken,
      errorClass: 'bootstrap',
      status: error?.status || 0,
      message: error?.message || '',
    });
    return { parsedDOMReplacementRules: DEFAULT_DOM_REPLACEMENT_RULES };
  }
}

// Create one shadow DOM container for the search modal
async function createSearchInstance(clientPublicToken) {
  if (hasSearchInstance) return;

  // Fetch the configuration first
  searchConfig = await fetchSearchConfig(clientPublicToken);

  // Apply container overrides from legacy snippet attributes
  // These take precedence over server-side configuration
  if (searchConfig && containerOverrides.resultLinkRootPath) {
    searchConfig.resultLinkRootPath = containerOverrides.resultLinkRootPath;
  }
  if (searchConfig && containerOverrides.refParam) {
    searchConfig.refParam = containerOverrides.refParam;
  }

  // Create container for the search modal that will be in shadow DOM
  searchContainer = document.createElement('div');
  searchContainer.className = 'vo-search-container';
  searchContainer.style.display = 'none';
  document.body.appendChild(searchContainer);

  const root = createRoot(searchContainer);
  root.render(
    <VoSearchShadowRoot
      clientPublicToken={clientPublicToken}
      ui={widgetUi}
      editorMode={false}
      hideTrigger={true}
    />
  );

  hasSearchInstance = true;
}

// Add trigger to open the search modal
// Uses module-level containerOverrides which can specify modalTriggerIconColor
// to override server config (e.g., for dashboard embedding)
function createTriggerButton(shape = null) {
  // Get colors from the config we fetched from the server
  let primaryColor = '#712cf9';
  let modalTriggerIconColor = '#ffffff';

  if (searchConfig) {
    primaryColor = searchConfig.primaryColor || primaryColor;
    modalTriggerIconColor = searchConfig.modalTriggerIconColor || modalTriggerIconColor;
  }

  // Allow container data attribute override (e.g., for dashboard embedding)
  if (containerOverrides.modalTriggerIconColor) {
    modalTriggerIconColor = containerOverrides.modalTriggerIconColor;
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'vo-search-search-modal-trigger';
  button.style.setProperty('--primary-color', primaryColor);
  button.style.setProperty('--modal-trigger-icon-color', modalTriggerIconColor);
  button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="${modalTriggerIconColor}" stroke-width="2" width="20" height="20" style="display: block;"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" fill="none"></path></svg>`;

  button.addEventListener('click', () => {
    openSearchWithShape(shape);
  });

  return button;
}

// Replace the target with a search trigger button
function replaceTrigger(target, shape = null) {
  if (target.nextSibling && target.nextSibling.classList?.contains('vo-search-trigger-container')) {
    target.nextSibling.remove();
  }

  const triggerContainer = document.createElement('div');
  triggerContainer.className = 'vo-search-trigger-container';

  const button = createTriggerButton(shape);
  triggerContainer.appendChild(button);

  target.parentNode.insertBefore(triggerContainer, target.nextSibling);
  target.style.display = 'none';
}

// Process a DOM replacement rule
function processRule(rule) {
  if (!rule.selector && !rule.mount) {
    debugWarn('[vo-search] Invalid rule, missing selector and mount:', rule);
    return false;
  }

  let found = false;

  // Resolve this placement's shape. An in-place surface mounts the widget where
  // it sits; an overlay surface wires a trigger that opens the shared overlay.
  const placementShape = resolvePlacementShape(rule, searchConfig);
  const placementRenderer = rendererForShape(placementShape);
  const placementNarrow = placementNarrowOverride(rule);

  // ...unless the placement scopes its overlay to itself, in which case it needs
  // a root of its own for that overlay to live in. An overlay surface wiring a
  // trigger and stopping there left nothing for the scope to name -- the trigger
  // drove the shared overlay on document.body, so overlayScope was a no-op for
  // exactly the placements that ask for it (decision #5067). Mounting a root
  // here gives the placement its own trigger and its own overlay; the shared one
  // stays untouched for every page-scoped placement, which is the default.
  const scopedToSelf = rule.overlayScope === 'self';
  const mountsInPlace =
    placementRenderer === 'inline' || placementRenderer === 'inpage' || scopedToSelf;
  // An add rule puts the widget beside the element it addresses rather than over
  // it, so the customer's own markup survives.
  const mountAt = rule.action === 'add'
    ? (rule.position === 'before' ? 'before' : 'after')
    : 'replace';
  // Which placement this is, so a re-run can recognise the host it already put
  // beside this target and leave it alone (decision #5605).
  const rulePlacementKey = placementKey(rule);

  placementTargets(rule).forEach(el => {
    if (mountsInPlace) {
      // The placement's own shape, not null: this is what lets a header popup
      // and an archive panel coexist on one instance.
      if (mountPlacement(el, placementShape, placementNarrow, rule.overlayScope, mountAt, rulePlacementKey)) {
        found = true;
      }
      return;
    }

    if (rule.action === 'replace') {
      if (rule.useDefaultIcon === false && rule.customHtml) {
        // Replace with custom HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = rule.customHtml;
        const customElement = tempDiv.firstElementChild;

        if (customElement) {
          // Add click handler to open the shared overlay in the resolved mode
          customElement.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            openSearchWithShape(placementShape);
          });

          // Make it look clickable
          customElement.style.cursor = 'pointer';

          // Replace the original element
          el.parentNode.replaceChild(customElement, el);
          found = true;
        }
      } else {
        // Use default icon replacement
        replaceTrigger(el, placementShape);
        found = true;
      }
    } else if (rule.action === 'add') {
      // The same guard the in-place mount uses, on the same helper. It used to
      // read raw siblings and match on the container's class alone, which was
      // wrong twice over: a text node between the container and its target hid
      // it, and two placements addressing one target from the same side
      // collapsed into one, because the first one's container answered the
      // second one's check and the second never wired a trigger at all.
      //
      // Already satisfied by an earlier pass is a match, not a miss (item
      // #8713). Reporting it as a miss made the bootstrap's re-run on tab
      // refocus conclude the instance had no placements at all and populate the
      // snippet container with a stray instance-shaped trigger.
      if (hasAdjacentPlacement(el, mountAt, rulePlacementKey)) {
        found = true;
        return;
      }

      const triggerContainer = document.createElement('div');
      triggerContainer.className = 'vo-search-trigger-container';
      if (rulePlacementKey) triggerContainer.dataset.voSearchPlacement = rulePlacementKey;

      if (rule.useDefaultIcon === false && rule.customHtml) {
        // Use custom HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = rule.customHtml;
        const customElement = tempDiv.firstElementChild;

        if (customElement) {
          // Add click handler to open the shared overlay in the resolved mode
          customElement.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            openSearchWithShape(placementShape);
          });

          // Make it look clickable
          customElement.style.cursor = 'pointer';

          triggerContainer.appendChild(customElement);
          if (rule.position === 'before') {
            el.parentNode.insertBefore(triggerContainer, el);
          } else {
            el.parentNode.insertBefore(triggerContainer, el.nextSibling);
          }
          found = true;
        }
      } else {
        // Use default icon
        const button = createTriggerButton(placementShape);
        triggerContainer.appendChild(button);
        if (rule.position === 'before') {
          el.parentNode.insertBefore(triggerContainer, el);
        } else {
          el.parentNode.insertBefore(triggerContainer, el.nextSibling);
        }
        found = true;
      }
    } else if (rule.action === 'clone') {
      // Handle direct clone without parent selector for non-Ghost elements
      if (!rule.parentSelector) {
        // The clone is a full copy, so it carries the class the selector
        // matched and becomes a target itself on the next pass: the match set
        // doubles per refocus rather than growing by a constant. Two things to
        // recognise, therefore -- a target that IS one of our clones, and a
        // target whose clone already sits beside it.
        if (
          rulePlacementKey &&
          (el.dataset?.voSearchPlacement === rulePlacementKey ||
            hasAdjacentPlacement(el, 'after', rulePlacementKey))
        ) {
          found = true;
          return;
        }

        // Create a clone of the element
        const clone = el.cloneNode(true);
        if (rulePlacementKey) clone.dataset.voSearchPlacement = rulePlacementKey;

        // Make it open the shared overlay in the resolved mode
        clone.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          openSearchWithShape(placementShape);
        });

        // Make it look clickable
        clone.style.cursor = 'pointer';

        // Insert the clone after the original
        el.parentNode.insertBefore(clone, el.nextSibling);

        // Hide the original
        el.style.display = 'none';

        found = true;
      } else {
        // Special handling for Ghost search (uses parentSelector)
        const formWrapper = el.closest(rule.parentSelector);
        if (formWrapper) {
          const searchFieldSelector = rule.searchFieldSelector || '.search-field';
          const searchButtonSelector = rule.searchButtonSelector || '.search-button';

          cloneGhostForm(rule, formWrapper, searchFieldSelector, searchButtonSelector, placementShape);
          found = true;
        }
      }
    }
  });

  return found;
}

// Separate function for cloning Ghost search forms
function cloneGhostForm(rule, formWrapper, searchFieldSelector, searchButtonSelector, shape = null) {
  if (!formWrapper) return;

  // Create a new wrapper to prevent sodo-search bindings
  const newFormWrapper = formWrapper.cloneNode(true);
  const newSearchField = newFormWrapper.querySelector(searchFieldSelector);
  const newSearchButton = newFormWrapper.querySelector(searchButtonSelector);

  if (!newSearchField) {
    debugWarn('[vo-search] No search field found in form wrapper with selector:', searchFieldSelector);
    return;
  }

  // Remove ghost search attributes to prevent sodo-search bindings
  newSearchField.removeAttribute('data-ghost-search');
  if (newSearchButton) {
    newSearchButton.removeAttribute('data-ghost-search');
  }

  // Replace the old wrapper
  formWrapper.parentNode.replaceChild(newFormWrapper, formWrapper);

  // Add click handlers
  newSearchField.addEventListener('click', (e) => {
    e.preventDefault();
    openSearchWithShape(shape);
  });

  if (newSearchButton) {
    newSearchButton.addEventListener('click', (e) => {
      e.preventDefault();
      openSearchWithShape(shape);
    });
  }
}

export async function initialize_search() {
  // Try script-tag embed first, then fall back to div container
  const config = getScriptConfig();

  if (!config) {
    console.error('Visiting Objects Search: No configuration found. Use either:');
    console.error('  Script tag: <script class="visitingobjects-search" data-public-token="..." src="..."></script>');
    console.error('  Or div: <div class="visitingobjects-search" data-public-token="..."></div>');
    console.error('  Or legacy: <div id="visitingobjects-search" data-public-token="..."></div>');
    return;
  }

  const {
    clientPublicToken,
    container: snippetContainer,
    resultLinkRootPath,
    resultLinkParam,
    overlayScope: pinnedOverlayScope = 'page',
  } = config;
  currentClientPublicToken = clientPublicToken;

  // Set container overrides from data attributes
  // These can override server-side config for legacy snippets
  containerOverrides = {};
  if (config.modalTriggerIconColor) {
    containerOverrides.modalTriggerIconColor = config.modalTriggerIconColor;
  }
  // Legacy result link overrides (for customers with existing snippets)
  if (resultLinkRootPath) {
    containerOverrides.resultLinkRootPath = resultLinkRootPath;
    debugLog('[vo-search] Using legacy resultLinkRootPath override:', resultLinkRootPath);
  }
  if (resultLinkParam) {
    containerOverrides.refParam = normalizeRefParam(resultLinkParam);
    debugLog('[vo-search] Using legacy refParam override:', containerOverrides.refParam);
  }
  if (config.shape) {
    containerOverrides.shape = config.shape;
    debugLog('[vo-search] Using first-party pinned shape:', config.shape);
  }
  if (config.previewMarker) {
    containerOverrides.previewMarker = config.previewMarker;
    debugLog('[vo-search] Preview marker present; this traffic will not be logged');
  }

  // Built once, before any mount, because every mount path below hands this
  // same object to the widget.
  widgetUi = widgetUiOverrides(containerOverrides);

  // A pinned container is a slot VO owns (decision #5050), so it is populated
  // directly and the instance's DOM replacement rules are skipped entirely: a
  // customer rule that happened to match VO app markup must not rewrite the
  // page around its own chrome. The widget renders whatever the pinned shape
  // asks for, including its own trigger and overlay when that shape has them.
  //
  // This runs BEFORE createSearchInstance because the pinned mount is
  // self-contained. Bootstrapping the shared overlay host as well would append
  // a second widget to document.body driven by the INSTANCE config rather than
  // the pinned shape -- for an inpage instance, a full always-open search at
  // the bottom of the page.
  if (containerOverrides.shape && snippetContainer) {
    // A shadow host already here means this bootstrap has run before -- it
    // re-runs on tab refocus -- and attachShadow on the same element twice
    // throws. Leaving the mounted widget alone is also better than rebuilding
    // it: a re-mount would throw away whatever the visitor had typed.
    if (snippetContainer.shadowRoot) return;

    snippetContainer.innerHTML = '';
    // The CONTAINER is the host, exactly as the demo bootstrap mounts (item
    // #8781). Going through mountPlacement inserted a .vo-search-inline-host
    // between the slot and the widget, and that wrapper is content-sized: a
    // scoped overlay laid out against it measured the trigger (36x48) instead
    // of the slot (818x560), so the same pinned container rendered a different
    // widget depending on which bundle read it.
    //
    // The container's own overlay scope, not the default: a slot that asked to
    // keep its overlay inside itself was getting a document.body overlay here
    // while the demo bundle honoured the same attribute.
    mountWidgetInShadowHost(snippetContainer, {
      overlayScope: pinnedOverlayScope,
      render: ({ portalTarget }) => (
        <VisitingObjectsSearch
          clientPublicToken={clientPublicToken}
          ui={widgetUi}
          shapeOverride={containerOverrides.shape}
          portalTarget={portalTarget}
        />
      ),
    });
    return;
  }

  // Create the search instance first and fetch the config
  if (!hasSearchInstance) {
    await createSearchInstance(clientPublicToken);
  }

  // Use custom rules only
  if (searchConfig && searchConfig.parsedDOMReplacementRules) {
    debugLog('[vo-search] Using custom DOM replacement rules');

    // Process the custom rules
    let found = false;
    for (const rule of searchConfig.parsedDOMReplacementRules) {
      const ruleFound = processRule(rule);
      found = found || ruleFound;
    }

    // If no rules matched and we have a container, populate it directly with
    // the instance shape: an in-place surface mounts the widget in the
    // container; an overlay surface adds a trigger button.
    //
    // "No rules matched" is not the same as "this instance renders nowhere",
    // and the difference shows up on the bootstrap's re-run at tab refocus
    // (item #8713). A named-mount placement mounts by REPLACING its mount
    // point, so on the second pass its selector matches nothing -- through no
    // fault of the page, and with the placement sitting right there. Falling
    // back then appends an instance-shaped trigger the customer never asked
    // for, once per refocus.
    if (!found && !hasMountedPlacement() && snippetContainer) {
      debugLog('[vo-search] No DOM elements matched rules, populating container directly');

      const defaultShape = resolvePlacementShape({}, searchConfig);
      const defaultRenderer = rendererForShape(defaultShape);
      snippetContainer.innerHTML = '';
      if (defaultRenderer === 'inline' || defaultRenderer === 'inpage') {
        const mountPoint = document.createElement('div');
        snippetContainer.appendChild(mountPoint);
        mountPlacement(mountPoint, defaultShape);
      } else {
        const button = createTriggerButton(defaultShape);
        snippetContainer.appendChild(button);
      }
    }
  }
}

/**
 * voSearchRemount rebuilds the widget from the mount element's current
 * attributes. It is a FIRST-PARTY hook (decision #5050): VO's own chrome -- the
 * instance preview's Desktop/Mobile toggle -- changes data-vo-search-shape and
 * needs the widget rebuilt from it. It is not part of the customer embed
 * contract, and a snippet never calls it.
 *
 * The container is cleared and the instance flag reset first, so remounting is
 * symmetric: pinning a shape and un-pinning it both produce a fresh widget
 * rather than a second one layered over the first.
 *
 * @returns {Promise<void>}
 */
export async function remount_search() {
  const config = getScriptConfig();
  if (config && config.container) config.container.innerHTML = '';
  hasSearchInstance = false;
  // A remount is a deliberate rebuild, so it re-reads the config rather than
  // serving the one shared from before the change that prompted it.
  resetSharedConfigCache();
  await initialize_search();
}

if (typeof window !== 'undefined') {
  window.voSearchRemount = remount_search;
}

// Use async IIFE to properly initialize with await
(async function () {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize_search);
  } else {
    await initialize_search();
  }
})();

export { VisitingObjectsSearch };

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    initialize_search();
  }
});

// Currently unreferenced, and wired anyway: a mount path that is an exception
// to the ui contract is how #16466 happened. If this is revived it must not
// silently log preview traffic.
const StandaloneSearch = ({ clientPublicToken, ui = {} }) => {
  const initialized = useRef(false);
  useEffect(() => {
    initialized.current = true;
    return () => { initialized.current = false; };
  }, []);
  return (
    <VisitingObjectsSearch
      clientPublicToken={clientPublicToken}
      ui={ui}
      editorMode={false}
    />
  );
};